/* eslint-disable */
import { defineStore } from "pinia";
import { applyPatch } from 'rfc6902';
import moment from "moment";
import _ from "lodash";
import { usePreSportsStore } from "@/stores/pre_sports";
import { useDatesStore } from "@/stores/dates";
import { useCustomerStore } from "./customer";

export function findBoostedOutcome(marketObj) {
	if (!marketObj || typeof marketObj !== "object") return null;

	// Check if this node is an outcome with campaign_id and valid prices
	if (
		marketObj.campaign_id != null &&
		marketObj.boosted_price != null &&
		marketObj.price != null &&
		!isNaN(Number(marketObj.boosted_price)) &&
		!isNaN(Number(marketObj.price))
	) {
		return marketObj;
	}

	for (const key of Object.keys(marketObj)) {
		const child = marketObj[key];
		if (child && typeof child === "object") {
			const found = findBoostedOutcome(child);
			if (found) return found;
		}
	}
	return null;
}

export function isBoostActive(config, fixture) {
	if (!fixture || fixture.live) return false;
	if (!fixture.overview_markets || !config?.market_slug) return false;
	const market = fixture.overview_markets[config.market_slug];
	if (!market) return false;
	return findBoostedOutcome(market) != null;
}

export function isFixtureExpired(config, fixture) {
	if (!fixture) return false;
	if (fixture.live) return true;
	if (fixture.start_datetime) {
		const expirationMinutes = Number(config?.expiration_minutes) || 0;
		const cutoffTime = moment.utc(fixture.start_datetime).subtract(expirationMinutes, "minutes");
		return moment.utc().isSameOrAfter(cutoffTime);
	}
	return false;
}

function formatMarketSlug(slug) {
	if (!slug) return "";
	return slug
		.replace(/_basketball|_soccer|_tennis/gi, "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (l) => l.toUpperCase());
}

export const useBetBoosterStore = defineStore("betBooster", {
	state: () => ({
		loading: false,
		selected_sport_id: "all",
		bet_boosted_fixtures: [],
		fixtures_data: {},
		current_timestamp: Date.now(),
		timer_interval: null,
		visibility_listener: null
	}),
	getters: {
		boosted_bets(state) {
			if (!state.bet_boosted_fixtures || !state.bet_boosted_fixtures.length) {
				return [];
			}

			// Reactive dependency to trigger re-computation on timer or tab focus
			const _ticker = state.current_timestamp;
			const datesStore = useDatesStore();
			const preSportsStore = usePreSportsStore();
			const list = [];

			for (const config of state.bet_boosted_fixtures) {
				const fixture = state.fixtures_data && state.fixtures_data[config.fixture_id];
				if (!fixture || fixture.live) continue;

				if (isFixtureExpired(config, fixture)) {
					continue;
				}

				if (!fixture.overview_markets || !config.market_slug) continue;

				const market = fixture.overview_markets[config.market_slug];
				if (!market) continue;

				// Find the boosted outcome with campaign_id and prices
				const outcome = findBoostedOutcome(market);
				if (!outcome) continue;

				const originalOdds = Number(outcome.price);
				const boostedOdds = Number(outcome.boosted_price);

				if (isNaN(originalOdds) || isNaN(boostedOdds) || originalOdds <= 0) continue;

				const diff = boostedOdds - originalOdds;
				const boostPercentage = Math.round((diff / originalOdds) * 100);
				const isSuperBoost = boostPercentage >= 20;

				const sportId = fixture.sport_id != null ? fixture.sport_id : config.sport_id;
				const participants = fixture.participants || [];
				const homeName = participants[0] || "";
				const awayName = participants[1] || "";
				const title = participants.length > 1 ? `${homeName} - ${awayName}` : (participants[0] || "");
				const competitionName = fixture.competition_name || "";

				let startTime = "";
				if (fixture.start_datetime) {
					const d = datesStore.preFixtureBookDate ? datesStore.preFixtureBookDate(fixture.start_datetime) : "";
					const t = datesStore.preFixtureBookTime ? datesStore.preFixtureBookTime(fixture.start_datetime) : "";
					startTime = d && t ? `${d} ${t}` : (d || t || "");
				}

				const sportName = fixture.sport_name || (preSportsStore.preSportName ? preSportsStore.preSportName(sportId) : "");
				const homeLogo = fixture.participant_logos?.home || null;
				const awayLogo = fixture.participant_logos?.away || null;

				const legs = [
					{
						id: outcome.id || outcome.uuid,
						title: outcome.name,
						market: outcome.market_name || formatMarketSlug(config.market_slug),
						badge: boostPercentage ? `+${boostPercentage}%` : null,
						icon: "rocket"
					}
				];

				list.push({
					id: config.fixture_id,
					fixture_id: config.fixture_id,
					campaign_id: outcome.campaign_id,
					config: config,
					fixture: fixture,
					outcome: outcome,
					odd_uuid: outcome.uuid || outcome.id,
					market_slug: config.market_slug,
					sport_id: sportId,
					sport_name: sportName,
					region_id: fixture.region_id,
					competition_id: fixture.competition_id,
					type: "match",
					competition: competitionName,
					competition_name: competitionName,
					title: title,
					participants: participants,
					participant_logos: fixture.participant_logos || null,
					jersey_colors: fixture.jersey_colors || null,
					start_datetime: fixture.start_datetime,
					start_time: startTime,
					team1: {
						name: homeName,
						logo: homeLogo,
						flag: homeLogo,
						jersey: fixture.jersey_colors?.home || null
					},
					team2: {
						name: awayName,
						logo: awayLogo,
						flag: awayLogo,
						jersey: fixture.jersey_colors?.away || null
					},
					original_odds: originalOdds,
					boosted_odds: boostedOdds,
					boost_percentage: boostPercentage,
					is_super_boost: isSuperBoost,
					legs: legs
				});
			}

			return _.sortBy(list, (item) => Number(item.sport_id));
		},
		availableSports() {
			const preSportsStore = usePreSportsStore();
			const sportsMap = new Map();

			this.boosted_bets.forEach((item) => {
				if (item.sport_id && !sportsMap.has(item.sport_id)) {
					const name = item.sport_name || (preSportsStore.preSportName ? preSportsStore.preSportName(item.sport_id) : "Sport " + item.sport_id);
					const icon = preSportsStore.preSportWhiteIcon ? preSportsStore.preSportWhiteIcon(item.sport_id) : null;
					sportsMap.set(item.sport_id, {
						id: item.sport_id,
						name: name,
						icon: icon
					});
				}
			});

			const sortedSports = Array.from(sportsMap.values()).sort((a, b) => Number(a.id) - Number(b.id));

			return [
				{
					id: "all",
					name: "All",
					icon_type: "diamond"
				},
				...sortedSports
			];
		},
		filteredBoostedBets(state) {
			if (!state.selected_sport_id || state.selected_sport_id === "all") {
				return _.sortBy([...this.boosted_bets], (item) => Number(item.sport_id));
			}
			
			return this.boosted_bets.filter(
				(item) => String(item.sport_id) === String(state.selected_sport_id)
			);
		}
	},
	actions: {
		setSelectedSport(sportId) {
			this.selected_sport_id = sportId;
		},
		fetchBoostedBets() {
			const customerStore = useCustomerStore();
			return customerStore.isLoggedIn ? this.fetchCustomerBoostedBets() : this.fetchGuestBoostedBets();
		},
		fetchGuestBoostedBets() {
			this.loading = true;
			const customerStore = useCustomerStore();

			return global.axios.get(process.env.VUE_APP_API_URL + '/bet-boost/guest/fixtures', {params: { utc_offset: customerStore.getCustomersUtcOffset }}).then((response) => {
				this.bet_boosted_fixtures = response.data.bet_boosted_fixtures || [];
				return response.data;
			}).catch((error) => {
				console.error("Failed to fetch boosted bets:", error);
				return null;
			}).finally(() => {
				this.loading = false;
			});
		},
		fetchCustomerBoostedBets() {
			this.loading = true;

			return global.axios.get(process.env.VUE_APP_API_URL + '/bet-boost/customer/fixtures').then((response) => {
				this.bet_boosted_fixtures = response.data.bet_boosted_fixtures || [];
				return response.data;
			}).catch((error) => {
				console.error("Failed to fetch boosted bets:", error);
				return null;
			}).finally(() => {
				this.loading = false;
			});
		},
		setFixture(data) {
			if (!data || !data.id) return;

			this.fixtures_data = {
				...this.fixtures_data,
				[data.id]: data
			};
		},
		updateFixture(data) {
			if (!data || !data.id) return;
			if (this.fixtures_data[data.id]) applyPatch(this.fixtures_data[data.id], data.patch);
		},
		deleteFixture(fixtureId) {
			const copy = { ...this.fixtures_data };
			delete copy[fixtureId];
			this.fixtures_data = copy;
		},
		updateTimestamp() {
			this.current_timestamp = Date.now();
		},
		startExpirationTimer() {
			if (this.timer_interval) return;

			this.updateTimestamp();

			this.timer_interval = setInterval(() => {
				this.updateTimestamp();
			}, 15000);

			if (typeof document !== "undefined") {
				this.visibility_listener = () => {
					if (document.visibilityState === "visible") {
						this.updateTimestamp();
					}
				};
				document.addEventListener("visibilitychange", this.visibility_listener);
			}
		},
		stopExpirationTimer() {
			if (this.timer_interval) {
				clearInterval(this.timer_interval);
				this.timer_interval = null;
			}
			if (this.visibility_listener && typeof document !== "undefined") {
				document.removeEventListener("visibilitychange", this.visibility_listener);
				this.visibility_listener = null;
			}
		}
	}
});
